import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, MapPin } from 'lucide-react';

const CLUBS = [
  {
    id: 'jeepduck',
    name: 'JeepDuck.Club',
    tagline: 'Where Rubber Ducks Hit the Road',
    description: 'Track your Jeep Ducks on their adventure across the country. Join the duck squad!',
    path: '/club/jeepduck',
    image: 'https://static.prod-images.emergentagent.com/jobs/cd26eb61-b2c7-4fdd-b89e-acf441c68876/images/32ee058f1da636dbd641b836bf3184fa6e64784b7a00eef98384678c8ca32b71.png',
    gradient: 'from-yellow-500 to-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-800',
    accentColor: 'text-amber-600',
  },
  {
    id: 'travelrock',
    name: 'TravelRock.Club',
    tagline: 'Spreading Joy One Rock at a Time',
    description: 'Paint a rock, set it free, and watch it travel the world. Track its colorful journey!',
    path: '/club/travelrock',
    image: 'https://static.prod-images.emergentagent.com/jobs/041d772e-3c9a-4d09-90a9-049becfc36a9/images/e9a1be339cabc466965efff30b294afa6a6ac96d89acd9471c411b38d25f79c2.png',
    gradient: 'from-pink-500 to-rose-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    textColor: 'text-pink-800',
    accentColor: 'text-pink-600',
  },
  {
    id: 'curi',
    name: 'CuriTheKitten.com',
    tagline: "Follow Curi's Adventures",
    description: "Join Curi the curious kitten as he explores the world and sends messages home to Julia.",
    path: '/club/curi',
    image: '/curi-images/Upscale_0047.jpg',
    gradient: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-800',
    accentColor: 'text-orange-600',
  },
  {
    id: 'guestbook',
    name: 'iWasHere.today',
    tagline: 'Every Place Has a Story',
    description: 'Discover guestbook locations near you, sign in, and see who else has visited. Leave your mark!',
    path: '/club/guestbook',
    image: 'https://static.prod-images.emergentagent.com/jobs/e126ff91-8a6a-4545-bdbb-44acec104dcb/images/49732186ef18a855ea47d71e8595fa43aa76eda5685b37ef28ddbdcd8387104d.png',
    gradient: 'from-emerald-500 to-teal-500',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    textColor: 'text-emerald-800',
    accentColor: 'text-emerald-600',
  },
  {
    id: 'kindness',
    name: 'KindnessProject',
    subdomain: '.app',
    tagline: 'Commit a Random Act of Kindness',
    description: 'Scan the card, do something kind, pass it on. Watch kindness travel city to city on the live map.',
    path: '/club/kindness',
    image: 'https://customer-assets-rejwkqb3.emergentagent.net/job_fc0afc2b-3c18-4c2f-acef-a992572ab432/artifacts/hwbxcf6f_image.png',
    imageFit: 'contain',
    imageBg: 'bg-amber-100',
    gradient: 'from-amber-500 to-yellow-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-900',
    accentColor: 'text-amber-700',
  },
];

export const ClubShowcase = ({ excludeClubId = null, title = 'Explore Our Communities' }) => {
  const navigate = useNavigate();

  // Pick 3 random clubs, excluding the current one if on a club page
  const displayClubs = useMemo(() => {
    const available = excludeClubId
      ? CLUBS.filter(c => c.id !== excludeClubId)
      : [...CLUBS];
    // Shuffle
    for (let i = available.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [available[i], available[j]] = [available[j], available[i]];
    }
    return available.slice(0, 3);
  }, [excludeClubId]);

  return (
    <section className="py-12 md:py-16" data-testid="club-showcase">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{title}</h2>
          <p className="text-gray-500 text-sm md:text-base">
            Join a community and track your items on adventures around the world
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {displayClubs.map((club) => (
            <div
              key={club.id}
              onClick={() => navigate(club.path)}
              className={`group cursor-pointer rounded-2xl overflow-hidden border ${club.borderColor} ${club.bgColor} shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
              data-testid={`club-card-${club.id}`}
            >
              {/* Club Image */}
              <div className={`h-44 overflow-hidden relative ${club.imageBg || ''}`}>
                <img
                  src={club.image}
                  alt={club.name}
                  className={`w-full h-full ${club.imageFit === 'contain' ? 'object-contain p-2' : 'object-cover'} group-hover:scale-105 transition-transform duration-500`}
                />
                {club.imageFit !== 'contain' && (
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/20 to-transparent`} />
                )}
              </div>

              {/* Club Info */}
              <div className="p-5">
                <h3 className={`font-bold text-lg ${club.textColor} mb-1 leading-tight`}>
                  {club.name}
                  {club.subdomain && (
                    <span className={`text-sm font-semibold opacity-70 ml-0.5`}>{club.subdomain}</span>
                  )}
                </h3>
                <p className={`text-sm ${club.accentColor} font-medium mb-2`}>
                  {club.tagline}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                  {club.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-semibold ${club.accentColor} group-hover:underline flex items-center gap-1`}>
                    Visit Club <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClubShowcase;
